import Component from 'vue-class-component';
import { Mixins, Ref, Watch } from 'vue-property-decorator';

import ToolsService from '@/providers/tools';
import DesignerElementsMixin from '../../designer-elements.mixin';
import { createQuillEditor } from '@/components/common/ed-collaborative-text-editor/collaborative-text.utils';
import { setQuillInlineStyle } from '@/components/common/ed-collaborative-text-editor/formats/attributors';

@Component({
  name: 'ed-designer-text',
  components: {
    EdCollaborativeText: () => import(/* EdCollaborativeText */ '@/components/common/ed-collaborative-text-editor/ed-collaborative-text.vue'),
  }
})
export default class EdDesignerTextComponent extends Mixins(DesignerElementsMixin) {
  isTyping = false;
  quillInstance = null;
  quillIsLoading = false;
  firstLoad = true;
  
  @Ref() readonly editor: any;

  get textContainerClass() {
    return {
      'selected': this.selected && !this.firstLoad,
      'is-typing': this.isTyping,
    }
  }

  mounted(): void {
    this.initTextElement();
  }

  async initTextElement() {
    this.DesignerStateProvider.isTextOnPreRender = true;
    this.setTextConfig();
    await ToolsService.sleep(100);
    this.setEditorOverflow();
  }

  selectElement() {
    this.DesignerStateProvider.isTextOnPreRender = false;

    this.firstLoad = false;
    this.setTextConfig();
  }

  setTextConfig() {
    if (!this.selected) {
      this.quillInstance = null;
    }

    if (!this.isTyping && !this.selected) {
      this.onSelectElement();
      this.startQuillLoading();
    }
  }

  async startQuillLoading() {
    if (!this.quillInstance) {
      this.quillIsLoading = true;
      await this.DesignerStateProvider.refreshToolbar();
    }
  }

  async setQuillInstance() {
    if (!this.quillInstance) {
      setQuillInlineStyle();
      this.quillInstance = createQuillEditor({
        editor: this.editor,
        toolbarId: this.DesignerStateProvider.quillTextToolbarId,
        placeholder: this.$i18n.t('designer.elements.text.placeholder').toString(),
      });
  
      this.DesignerStateProvider.setQuillInstance(this.quillInstance);
      this.quillIsLoading = false;

      // Verify if it's a legacy text element.
      if (typeof this.value.meta?.content === 'string')  {
        this.quillInstance.root.innerHTML = this.value.meta?.content;
      } else {
        this.quillInstance?.setContents(this.value.meta?.content || []);
      }
  
      this.quillInstance?.on('text-change', () => {
        const { ops } = this.quillInstance.getContents();
        const hasContentOnFirstLine = (ops.length === 1 && !!ops[0].insert.replace(/\n/g, '').replace(/ /g, ''));
        const hasContent = ops.length > 1 || hasContentOnFirstLine;

        this.value.sizes.height = this.elementDesigner.clientHeight;
        this.value.sizes.width = this.elementDesigner.clientWidth;
        this.value.meta = {
          content: hasContent ? ops : [],
        };
      });
    }
  }

  activateEditor() {
    this.DesignerStateProvider.isTextOnPreRender = false;
    this.value.turnOffDraggable();
    this.isTyping = true;
    this.quillInstance.focus();
    this.setEditorOverflow('auto');
  }

  setEditorOverflow(value = 'hidden') {
    const editor = this.editor.querySelector('.ql-editor');
    editor.style.overflowY = value;
  }

  removeElement() {
    if (!this.isTyping) {
      this.onRemoveElement();
    }
  }

  onUnselectTextElement() {
    this.value.turnOnDraggable();
    this.isTyping = false;
    this.quillInstance = null;
    this.setEditorOverflow();
  }

  @Watch('selected')
  onSelectedChanged() {
    if (!this.selected) {
      this.onUnselectTextElement();
    }
  }

  @Watch('DesignerStateProvider.quillIsReady')
  onQuillIsReadyChanged() {
    if (this.DesignerStateProvider.quillIsReady && this.quillIsLoading) {
      this.setQuillInstance();
    }
  }
  

}